import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import GlassCard from "@/components/GlassCard";
import ScoreTrendChart from "@/components/ScoreTrendChart";
import ShareableScoreCard from "@/components/ShareableScoreCard";
import { BlurText, SpotlightCard, ShinyText, ClickSpark, Magnet, CountUp, AnimatedList } from "@/components/animations";
import useMotionPreferences from "@/hooks/useMotionPreferences";
import api from "@/lib/api";
import { tierMeta, TIER_COLORS, indicativeLimit } from "@/lib/tiers";
import { cohortLabel, computeCohortAvg, formatINR } from "@/lib/format";
import { ModelSwitch, GenerationMeta, RegeneratingVeil, FaithfulnessPanel } from "@/components/ExplanationModelSwitch";
import ScoreLevers from "@/components/ScoreLevers";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { EASING, DURATION } from "@/lib/motionSystem";
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine
} from "recharts";
import {
    Shield, TrendingUp, Brain, Users, Share2,
    FileText, ArrowUpRight, Copy, Download, Layers,
    CreditCard, ChevronRight, ChevronDown, Sparkles, CheckCircle2,
    AlertCircle, Eye, Zap, ArrowRight, Landmark, Database, Award, Clock3
} from "lucide-react";

const SIGNAL_META = {
    academic: { label: "Academic Intelligence", color: "#3b82f6", icon: "GraduationCap" },
    fee_payment: { label: "Fee Payment History", color: "#06b6d4", icon: "CreditCard" },
    developer_professional: { label: "Developer / Professional", color: "#8b5cf6", icon: "Code" },
    social_creator: { label: "Social / Creator", color: "#f59e0b", icon: "Share" },
    consumption: { label: "Consumption Intelligence", color: "#fb923c", icon: "TrendingUp" },
    coverage_consistency: { label: "Coverage & Consistency", color: "#10b981", icon: "Layers" },
};


const SOURCE_BADGES = {
    oauth: { label: "OAuth", color: "bg-green-pioneer/20 text-green-pioneer" },
    verified_system: { label: "Verified", color: "bg-green-pioneer/20 text-green-pioneer" },
    uploaded_document: { label: "Uploaded", color: "bg-blue-primary/20 text-blue-primary" },
    url_ingest: { label: "URL Extracted", color: "bg-cyan-signal/20 text-cyan-signal" },
    manual: { label: "Self-declared", color: "bg-white/10 text-text-secondary" },
};

// The reveal is theatre, not progress: the data is usually already in hand. At
// 800ms a step it held the score back for 3.8s, which is a long time to stare at
// a loading screen when someone is watching over your shoulder.
const REVEAL_STEP_MS = 380;
const REVEAL_TOTAL_MS = 4 * REVEAL_STEP_MS + 320;

const REVEAL_STEPS = [
    "Verifying evidence...",
    "Analyzing signals...",
    "Computing score...",
    "Generating insights...",
];

function AnimatedScore({ target, tier }) {
    const baseline = Math.max(300, (target || 300) - 220);
    const [val, setVal] = useState(baseline);
    const tc = tierMeta(tier);

    useEffect(() => {
        if (!target) return;
        setVal(baseline);
        const dur = DURATION.scoreReveal * 1000;
        const t0 = performance.now();
        let raf = 0;
        function tick(now) {
            const p = Math.min((now - t0) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.floor(baseline + (target - baseline) * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        }
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [baseline, target]);

    return (
        <motion.span
            className="font-mono text-6xl sm:text-7xl lg:text-8xl font-bold tracking-tighter"
            style={{ color: tc.color, textShadow: `0 0 40px ${tc.glow}, 0 0 80px ${tc.glow.replace("0.4", "0.15")}` }}
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: DURATION.scoreReveal, ease: EASING.emphasize }}
            data-testid="score-value"
        >
            {val}
        </motion.span>
    );
}

function ScoreGauge({ score, tier }) {
    const pct = Math.max(0, Math.min(1, (score - 300) / 600));
    const tc = tierMeta(tier);
    const r = 80;
    const arcLen = Math.PI * r;
    const [offset, setOffset] = useState(arcLen);
    const [complete, setComplete] = useState(false);

    useEffect(() => {
        setComplete(false);
        const timer = setTimeout(() => setOffset(arcLen * (1 - pct)), 160);
        const done = setTimeout(() => setComplete(true), DURATION.scoreReveal * 1000);
        return () => {
            clearTimeout(timer);
            clearTimeout(done);
        };
    }, [pct, arcLen]);

    return (
        <svg viewBox="0 0 200 115" className="w-full max-w-[260px] mx-auto" data-testid="score-gauge">
            <defs>
                <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
            </defs>
            <path d="M 10 105 A 80 80 0 0 1 190 105" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" strokeLinecap="round" />
            <path d="M 10 105 A 80 80 0 0 1 190 105" fill="none" stroke={tc.color} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={arcLen} strokeDashoffset={offset}
                  style={{ transition: `stroke-dashoffset ${DURATION.scoreReveal}s cubic-bezier(0.16,1,0.3,1)` }}
                  filter="url(#glow)" />
            {complete && (
                <motion.circle
                    cx="190"
                    cy="105"
                    r="5"
                    fill={tc.color}
                    initial={{ scale: 0.2, opacity: 0.4 }}
                    animate={{ scale: [0.2, 1.6, 1], opacity: [0.4, 0.9, 0.75] }}
                    transition={{ duration: 0.65, ease: EASING.emphasize }}
                />
            )}
            <text x="10" y="112" className="fill-text-secondary" fontSize="9" fontFamily="JetBrains Mono">300</text>
            <text x="175" y="112" className="fill-text-secondary" fontSize="9" fontFamily="JetBrains Mono">900</text>
        </svg>
    );
}

function RevealOverlay({ onComplete }) {
    const [step, setStep] = useState(0);
    // Read the callback through a ref so an unstable prop identity can never
    // restart the countdown. Before this, every parent re-render (setData,
    // setEvidence, setLoading) tore the timers down and started them again,
    // so the reveal finished ~3.8s after the LAST render rather than the first.
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useEffect(() => {
        const timers = REVEAL_STEPS.map((_, i) =>
            setTimeout(() => setStep(i + 1), (i + 1) * REVEAL_STEP_MS)
        );
        const done = setTimeout(() => onCompleteRef.current?.(), REVEAL_TOTAL_MS);
        return () => { timers.forEach(clearTimeout); clearTimeout(done); };
    }, []);

    return (
        <motion.div
            className="fixed inset-0 z-50 bg-void/95 backdrop-blur-2xl flex items-center justify-center"
            exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
            data-testid="reveal-overlay"
        >
            <div className="text-center">
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="mb-8">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-primary/20 flex items-center justify-center mb-4">
                        <Brain size={28} className="text-blue-primary animate-pulse" />
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl font-semibold text-text-primary">Calculating your score</h2>
                </motion.div>
                <div className="space-y-3 max-w-xs mx-auto">
                    {REVEAL_STEPS.map((s, i) => (
                        <motion.div key={i}
                            initial={{ opacity: 0, x: -10 }} animate={{ opacity: step > i ? 1 : 0.3, x: 0 }}
                            transition={{ delay: i * 0.15 }}
                            className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${step > i ? "bg-green-pioneer/20" : "bg-white/5"}`}>
                                {step > i ? <CheckCircle2 size={12} className="text-green-pioneer" /> : <div className="w-2 h-2 rounded-full bg-white/20" />}
                            </div>
                            <span className={`text-sm ${step > i ? "text-text-primary" : "text-text-secondary/50"}`}>{s}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.div>
    );
}

function SignalDonut({ signals }) {
    const data = Object.entries(signals || {}).map(([key, val]) => ({
        name: SIGNAL_META[key]?.label || key,
        value: val.weight * 100,
        score: val.score,
        fill: SIGNAL_META[key]?.color || "#666",
    }));

    return (
        <ResponsiveContainer width="100%" height={220}>
            <PieChart>
                <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                     paddingAngle={3} dataKey="value" stroke="none">
                    {data.map((d, i) => <Cell key={i} fill={d.fill} opacity={0.85} />)}
                </Pie>
                <Tooltip
                    contentStyle={{ background: "#0d1526", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", fontSize: "13px" }}
                    itemStyle={{ color: "#f1f5f9" }}
                    formatter={(value, name, props) => [`${props.payload.score}/100 (${value}% weight)`, name]}
                />
            </PieChart>
        </ResponsiveContainer>
    );
}

function SignalDrillDown({ signalKey, data, expanded, onToggle }) {
    const meta = SIGNAL_META[signalKey] || { label: signalKey, color: "#666" };
    const positives = data?.top_positives || [];
    const negatives = data?.top_negatives || [];
    const score = data?.score || 0;
    const evidenceTypes = data?.evidence_types || [];

    const sourceBadge = (type) => {
        const map = {
            oauth: { label: "OAuth Verified", cls: "bg-green-pioneer/15 text-green-pioneer" },
            verified_system: { label: "Verified", cls: "bg-green-pioneer/15 text-green-pioneer" },
            url_ingest: { label: "URL Extracted", cls: "bg-cyan-signal/15 text-cyan-signal" },
            uploaded_document: { label: "Uploaded", cls: "bg-blue-primary/15 text-blue-primary" },
            user_declared: { label: "Self-declared", cls: "bg-white/10 text-text-secondary" },
            manual: { label: "Self-declared", cls: "bg-white/10 text-text-secondary" },
            inferred: { label: "Inferred", cls: "bg-purple-soft/15 text-purple-soft" },
        };
        return map[type] || map.inferred;
    };

    return (
        <SpotlightCard
            className="p-4 cursor-pointer"
            spotlightColor={`${meta.color}33`}
            data-testid={`signal-${signalKey}`}
            onClick={onToggle}
        >
            {/* Collapsed header — always visible */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: meta.color }} />
                    <span className="text-sm font-medium text-text-primary">{meta.label}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-bold" style={{ color: meta.color }}>{score}</span>
                    <span className="text-text-secondary text-xs">/100</span>
                    <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown size={16} className="text-text-secondary/50" />
                    </motion.div>
                </div>
            </div>
            {/* Mini progress bar — always visible */}
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                <motion.div className="h-full rounded-full" style={{ backgroundColor: meta.color }}
                    initial={{ width: 0 }} whileInView={{ width: `${score}%` }}
                    viewport={{ once: true }} transition={{ duration: 1, ease: "easeOut" }} />
            </div>

            {/* Expandable detail section */}
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="pt-3 mt-3 border-t border-white/5">
                            {evidenceTypes.length > 0 && (
                                <AnimatedList
                                    items={evidenceTypes.map((value, idx) => ({ id: `ev-${idx}`, value }))}
                                    animationType="scale"
                                    className="flex flex-wrap gap-1.5 mb-3"
                                    renderItem={(item) => {
                                        const b = sourceBadge(item.value);
                                        return <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>;
                                    }}
                                />
                            )}
                            {positives.length > 0 && (
                                <AnimatedList
                                    items={positives.map((value, idx) => ({ id: `pos-${idx}`, value }))}
                                    className="space-y-1 mb-2"
                                    renderItem={(item) => (
                                        <div className="flex items-start gap-2 text-xs">
                                            <CheckCircle2 size={12} className="text-green-pioneer shrink-0 mt-0.5" />
                                            <span className="text-green-pioneer/80">{item.value}</span>
                                        </div>
                                    )}
                                />
                            )}
                            {negatives.length > 0 && (
                                <AnimatedList
                                    items={negatives.map((value, idx) => ({ id: `neg-${idx}`, value }))}
                                    className="space-y-1"
                                    renderItem={(item) => (
                                        <div className="flex items-start gap-2 text-xs">
                                            <AlertCircle size={12} className="text-red-starter shrink-0 mt-0.5" />
                                            <span className="text-red-starter/80">{item.value}</span>
                                        </div>
                                    )}
                                />
                            )}
                            {positives.length === 0 && negatives.length === 0 && evidenceTypes.length === 0 && (
                                <p className="text-text-secondary/50 text-xs">No evidence data available for this signal.</p>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </SpotlightCard>
    );
}

function PeerComparisonBar({ score, tier, persona }) {
    const nationalAvg = 600;
    // Real cohort average, or null when the backend did not send one. Previously
    // `a || b ? 680 : national`, which parses as `(a || b) ? 680 : national` and
    // therefore showed a hardcoded 680 for everyone who matched any cohort.
    const cohortAvg = computeCohortAvg(persona);
    const barData = [
        { name: "Your Score", value: score, fill: tierMeta(tier).color },
    ];

    return (
        <div data-testid="peer-comparison">
            {/* height=130 + top margin=32 give the "National Avg" label room above the bar */}
            <ResponsiveContainer width="100%" height={130}>
                <BarChart data={barData} layout="vertical" margin={{ top: 32, right: 30, left: 10, bottom: 10 }}>
                    <XAxis type="number" domain={[300, 900]} tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }}
                           axisLine={{ stroke: "rgba(255,255,255,0.05)" }} tickLine={false} />
                    <YAxis type="category" dataKey="name" hide />
                    <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={24}>
                        {barData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                    <ReferenceLine x={nationalAvg} stroke="#94a3b8" strokeDasharray="4 4"
                        label={{ value: `National Avg (${nationalAvg})`, position: "insideTopLeft", offset: 8, fill: "#94a3b8", fontSize: 10 }} />
                    {cohortAvg !== null && Math.abs(cohortAvg - nationalAvg) > 15 && (
                        <ReferenceLine x={cohortAvg} stroke="#06b6d4" strokeDasharray="4 4"
                            label={{ value: `Cohort Avg (${cohortAvg})`, position: "insideBottomLeft", offset: 8, fill: "#06b6d4", fontSize: 10 }} />
                    )}
                    <Tooltip contentStyle={{ background: "#0d1526", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", fontSize: "13px" }}
                             itemStyle={{ color: "#f1f5f9" }} formatter={(v) => [`${v}/900`]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

export default function ScoreDashboard() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { prefersReducedMotion, isTouch } = useMotionPreferences();
    const isDemo = id?.startsWith("demo-");
    const demoName = isDemo ? id.replace("demo-", "") : null;

    const [data, setData] = useState(null);
    const [evidence, setEvidence] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReveal, setShowReveal] = useState(true);
    const [showShareCard, setShowShareCard] = useState(false);
    const [error, setError] = useState(null);
    const [expandedSignal, setExpandedSignal] = useState(null);

    // Live model narration. Only the demo profiles expose an unauthenticated
    // regeneration endpoint, so the switch is offered there and nowhere else.
    const [providers, setProviders] = useState([]);
    // Null until the presenter picks one: the initial narrative is the static
    // payload, which no live model produced.
    const [activeProvider, setActiveProvider] = useState(null);
    const [regenerating, setRegenerating] = useState(false);
    const [liveGeneration, setLiveGeneration] = useState(null);
    const [faithfulness, setFaithfulness] = useState(null);
    const [regenError, setRegenError] = useState(null);
    const [scorecard, setScorecard] = useState(null);
    // The counterfactual levers. Demo responses carry them inline; a recorded
    // score has to ask for them, because the serializer deliberately keeps the
    // heavy blocks off the main payload.
    const [counterfactual, setCounterfactual] = useState(null);

    useEffect(() => {
        if (prefersReducedMotion) {
            setShowReveal(false);
        }
    }, [prefersReducedMotion]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let res;
                if (isDemo) {
                    res = await api.get(`/api/score/demo/${demoName}`);
                } else {
                    res = await api.get(`/api/score/${id}`);
                }
                setData(res.data);
                if (res.data?.counterfactual) setCounterfactual(res.data.counterfactual);
                // Fetch evidence to detect verified sources (DigiLocker, iWebTechno)
                if (!isDemo) {
                    try {
                        const evRes = await api.get("/api/evidence");
                        const list = Array.isArray(evRes.data) ? evRes.data : (evRes.data?.evidence || []);
                        setEvidence(Array.isArray(list) ? list : []);
                    } catch { /* non-fatal */ }
                    try {
                        const cf = await api.get(`/api/score/${id}/counterfactual`, { timeout: 8000 });
                        setCounterfactual(cf.data || null);
                    } catch { /* non-fatal: the panel simply does not render */ }
                }
            } catch (err) {
                setError(err.response?.data?.detail || "Failed to load score data");
            }
            setLoading(false);
        };
        fetchData();
    }, [id, isDemo, demoName]);

    // Which models are actually usable right now. Anything the backend reports
    // as unconfigured is simply not offered, so the control can never present a
    // button that is guaranteed to fail.
    useEffect(() => {
        if (!isDemo) return;
        let cancelled = false;
        api.get("/api/version", { timeout: 8000 })
            .then(({ data: v }) => {
                if (cancelled) return;
                const roster = v?.ai_providers?.providers || {};
                const usable = Object.entries(roster)
                    .filter(([, meta]) => meta?.configured)
                    .map(([name, meta]) => ({ name, model: meta?.model }));
                setProviders(usable);
                // Display-only. The demo endpoints deliberately do not recompute
                // the score, so offering a scorecard *switch* here would be a
                // control that changes nothing — worse than no control at all in
                // front of an audience that will ask what it does.
                setScorecard(v?.model_version || null);
            })
            .catch(() => { /* control simply does not appear */ });
        return () => { cancelled = true; };
    }, [isDemo]);

    const switchProvider = useCallback(async (name) => {
        if (!demoName || name === activeProvider || regenerating) return;
        setRegenerating(true);
        setRegenError(null);
        const previous = activeProvider;
        setActiveProvider(name);
        try {
            // Generous timeout on purpose: a cold Gemini call has been measured
            // at ~19s, and timing out early would look like a product failure
            // rather than a slow model.
            const { data: fresh } = await api.get(
                `/api/score/demo/${demoName}`,
                { params: { live: true, provider: name }, timeout: 90000 }
            );
            // Replace ONLY the narrative. Score, tier and signals are untouched,
            // which is the property being demonstrated.
            setData(prev => (prev ? { ...prev, ai_explanation: fresh?.ai_explanation ?? prev.ai_explanation } : prev));
            setLiveGeneration(fresh?.live_generation || null);
            setFaithfulness(fresh?.faithfulness || null);
        } catch (err) {
            setActiveProvider(previous);
            setRegenError(
                err?.response?.status === 429
                    ? "Rate limited — wait a minute and try again."
                    : "Couldn't reach that model. Showing the previous narrative."
            );
        } finally {
            setRegenerating(false);
        }
    }, [demoName, activeProvider, regenerating]);

    // Detect verified integrations
    const evList = Array.isArray(evidence) ? evidence : [];
    const hasDigiLocker = evList.some(e => e?.source_type === "digilocker" || (e?.raw_data || {})._source === "digilocker");
    const hasIWebTechno = evList.some(e => (e?.raw_data || {})._source === "iwebtechno_erp" || (e?.processed_data || {})._source === "iwebtechno_erp")
        || (isDemo && demoName === "priya"); // priya demo is iWebTechno verified
    const consumptionData = data?.consumption_data || null;

    const tc = tierMeta(data?.tier);
    const persona = data?.persona_match;
    const ai = data?.ai_explanation;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Score link copied to clipboard");
    };

    const handleExportPDF = () => {
        if (!data) return;
        try {
            const doc = new jsPDF();
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.text("GenZ Credit Score Report", 20, 25);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}`, 20, 35);
            doc.setDrawColor(59, 130, 246);
            doc.line(20, 40, 190, 40);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(36);
            doc.text(`${data.score}`, 20, 60);
            doc.setFontSize(14);
            doc.text(`${data.tier} Tier`, 65, 60);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.text(`Confidence: ${(data.confidence * 100).toFixed(0)}%`, 20, 70);
            if (data.name) doc.text(`Profile: ${data.name}`, 20, 78);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text("Signal Scores", 20, 95);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            let y = 105;
            Object.entries(data.signal_scores || {}).forEach(([key, val]) => {
                const label = SIGNAL_META[key]?.label || key;
                doc.text(`${label}: ${val.score}/100 (${(val.weight * 100).toFixed(0)}% weight)`, 25, y);
                y += 8;
            });

            // Same precedence as the page: the computed levers win over the
            // model's suggestions. Exporting a different ordering from the one
            // on screen would hand someone two answers to the same question,
            // which is the exact confusion the panel exists to remove.
            const pdfLevers = (counterfactual?.levers || []).filter(l => (Number(l?.gain_if_perfect) || 0) > 0);
            if (pdfLevers.length) {
                y += 8;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.text("What would move this score", 20, y);
                y += 10;
                doc.setFont("helvetica", "normal");
                doc.setFontSize(11);
                pdfLevers.slice(0, 3).forEach((l, i) => {
                    const unlock = l.unlocks_tier ? ` (reaches ${l.score_if_perfect}, unlocks ${l.tier_if_perfect})` : "";
                    doc.text(`${i + 1}. +${l.gain_if_perfect}${unlock}`, 25, y);
                    y += 7;
                    doc.splitTextToSize(l.action || l.label, 160).forEach((line) => {
                        doc.text(line, 30, y);
                        y += 6;
                    });
                    y += 2;
                });
                if (counterfactual?.caveat) {
                    doc.setFontSize(9);
                    doc.setTextColor(110, 110, 110);
                    doc.splitTextToSize(counterfactual.caveat, 170).forEach((line) => {
                        doc.text(line, 20, y);
                        y += 5;
                    });
                    doc.setTextColor(0, 0, 0);
                    doc.setFontSize(11);
                }
            } else if (ai?.improvement_suggestions?.length) {
                y += 8;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.text("Top Improvement Suggestions", 20, y);
                y += 10;
                doc.setFont("helvetica", "normal");
                doc.setFontSize(11);
                ai.improvement_suggestions.slice(0, 3).forEach((s, i) => {
                    doc.text(`${i + 1}. ${s}`, 25, y);
                    y += 8;
                });
            }

            doc.setFontSize(9);
            doc.setTextColor(128, 128, 128);
            doc.text("GenZ Credit Score - Alternative Credit Assessment for India's Next Generation", 20, 280);

            doc.save(`genzcredit-${data.score}-${data.tier?.toLowerCase()}.pdf`);
            toast.success("PDF exported successfully");
        } catch (e) {
            toast.error("PDF export failed");
        }
    };

    const tierGlow = tc.shadow;

    if (error && !loading) {
        return (
            <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh flex items-center justify-center" data-testid="score-dashboard">
                <GlassCard className="p-8 text-center max-w-md">
                    <AlertCircle size={32} className="text-red-starter mx-auto mb-4" />
                    <h2 className="font-heading text-xl font-semibold text-text-primary mb-2">Score Not Found</h2>
                    <p className="text-text-secondary text-sm mb-6">{error}</p>
                    <Link to="/genz-credit-scoring" className="text-blue-primary text-sm font-medium">Go back to scoring</Link>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="pt-16 min-h-dvh" data-testid="score-dashboard">
            <AnimatePresence>
                {(loading || showReveal) && !error && (
                    <RevealOverlay onComplete={() => setShowReveal(false)} />
                )}
            </AnimatePresence>

            {data && !showReveal && (
                <div className="px-6 md:px-12 lg:px-24 pt-8 pb-16">
                    {/* Demo Switcher */}
                    {isDemo && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            className="max-w-6xl mx-auto mb-6" data-testid="demo-switcher">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-text-secondary text-xs uppercase tracking-widest font-semibold">Viewing Demo:</span>
                                {["priya", "rahul", "arjun"].map(name => (
                                    <button key={name}
                                        onClick={() => navigate(`/genz-credit-scoring/result/demo-${name}`)}
                                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                            demoName === name
                                                ? "bg-blue-primary/20 text-blue-primary border border-blue-primary/30"
                                                : "bg-white/5 text-text-secondary hover:text-text-primary border border-white/5"
                                        }`}
                                        data-testid={`switch-${name}`}
                                    >
                                        {name.charAt(0).toUpperCase() + name.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    <div className="max-w-6xl mx-auto">
                        {/* Hero Score Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0, scale: [1, 1.006, 1] }}
                            transition={{ delay: 0.1, duration: DURATION.sectionReveal, ease: EASING.smooth, repeat: Infinity, repeatDelay: 2.8 }}
                        >
                            <GlassCard className={`p-8 md:p-10 border ${tc.border} ${tierGlow} mb-6`} hover={false} delay={0}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                                    <div className="text-center md:text-left">
                                        {data.name && <p className="text-text-secondary text-sm mb-2">{data.name}'s Score</p>}
                                        <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-gradient-to-r from-purple-500/20 to-indigo-500/15 text-purple-soft border border-purple-soft/25 shadow-[0_0_16px_rgba(168,85,247,0.2)] animate-pulse">
                                                <Brain size={12} /> AI-Powered Score
                                            </span>
                                        </div>
                                        <AnimatedScore target={data.score} tier={data.tier} />
                                        <div className="mt-3 flex items-center gap-3 justify-center md:justify-start flex-wrap">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${tc.bg} ${tc.text}`}>
                                                <Shield size={14} /> {data.tier}
                                            </span>
                                            <CountUp target={Math.round((data.confidence || 0) * 100)} suffix="% confidence" className="text-text-secondary text-sm" duration={1.2} />
                                            {scorecard && (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono bg-white/[0.04] text-text-secondary/70 border border-white/10" title="The weighting model this score was computed with" data-testid="badge-scorecard">
                                                    scorecard {scorecard}
                                                </span>
                                            )}
                                            {hasDigiLocker && (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-amber-400/20 to-yellow-500/15 text-amber-300 border border-amber-400/25 shadow-[0_0_16px_rgba(251,191,36,0.2)]" data-testid="badge-digilocker">
                                                    <Landmark size={11} /> DigiLocker Verified
                                                </span>
                                            )}
                                            {hasIWebTechno && (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-primary/15 text-blue-primary border border-blue-primary/25" data-testid="badge-iwebtechno">
                                                    <Database size={11} /> iWebTechno Verified
                                                </span>
                                            )}
                                        </div>
                                        {data.created_at && (
                                            <p className="text-text-secondary/50 text-xs mt-3">
                                                Generated {new Date(data.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
                                            </p>
                                        )}
                                        {persona?.is_inferred && (
                                            <span className="inline-flex items-center gap-1 text-xs text-purple-soft/70 mt-2">
                                                <Eye size={11} /> Includes persona estimate
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex justify-center relative">
                                        {!prefersReducedMotion && !isTouch && (
                                            <video
                                                className="absolute inset-0 w-full h-full object-contain opacity-20 pointer-events-none"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                preload="metadata"
                                                poster="/media/score-reveal-poster.jpg"
                                            >
                                                <source src="/media/score-reveal.webm" type="video/webm" />
                                            </video>
                                        )}
                                        <ScoreGauge score={data.score} tier={data.tier} />
                                    </div>
                                </div>
                            </GlassCard>
                        </motion.div>

                        {/* Signal Breakdown + Drill Downs */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                                <GlassCard className="p-6 h-full" hover={false} delay={0}>
                                    <h3 className="font-heading text-lg font-medium text-text-primary mb-4">Signal Breakdown</h3>
                                    <SignalDonut signals={data.signal_scores} />
                                    <div className="mt-4 space-y-2">
                                        {Object.entries(data.signal_scores || {}).map(([key, val]) => (
                                            <div key={key} className="flex items-center justify-between text-xs">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SIGNAL_META[key]?.color }} />
                                                    <span className="text-text-secondary">{SIGNAL_META[key]?.label || key}</span>
                                                </div>
                                                <span className="font-mono font-bold" style={{ color: SIGNAL_META[key]?.color }}>{val.score}</span>
                                            </div>
                                        ))}
                                    </div>
                                </GlassCard>
                            </motion.div>

                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                                className="lg:col-span-2">
                                <GlassCard className="p-6 h-full" hover={false} delay={0}>
                                    <h3 className="font-heading text-lg font-medium text-text-primary mb-4">Signal Details</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {Object.entries(data.signal_scores || {}).map(([key, val]) => (
                                            <SignalDrillDown key={key} signalKey={key} data={val}
                                                expanded={expandedSignal === key}
                                                onToggle={() => setExpandedSignal(expandedSignal === key ? null : key)} />
                                        ))}
                                    </div>
                                </GlassCard>
                            </motion.div>
                        </div>

                        {/* Consumption Intelligence (shown when ecom data present) */}
                        {consumptionData && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mb-6">
                                <GlassCard className="p-6 border border-orange-400/20 bg-gradient-to-br from-orange-500/[0.03] via-transparent to-purple-500/[0.03]" hover={false} delay={0}>
                                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                        <div className="flex items-center gap-2">
                                            <div className="w-9 h-9 rounded-xl bg-orange-400/15 border border-orange-400/25 flex items-center justify-center">
                                                <TrendingUp size={16} className="text-orange-400" />
                                            </div>
                                            <div>
                                                <h3 className="font-heading text-lg font-medium text-text-primary">Consumption Intelligence</h3>
                                                <p className="text-text-secondary text-xs">Your spending patterns unlocked +{consumptionData.total_consumption_uplift || 0} pts</p>
                                            </div>
                                        </div>
                                        {data.signal_scores?.consumption && (
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-2xl font-bold text-orange-400">{data.signal_scores.consumption.score}</span>
                                                <span className="text-text-secondary text-xs">/ 100</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                                            <p className="text-[10px] uppercase tracking-widest text-text-secondary/60 mb-1">Monthly Ecom Spend</p>
                                            <p className="font-mono text-2xl font-bold text-text-primary">₹{(consumptionData.monthly_spend_avg || 0).toLocaleString("en-IN")}</p>
                                            <p className="text-text-secondary text-[11px] mt-0.5">last 90 days avg</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                                            <p className="text-[10px] uppercase tracking-widest text-text-secondary/60 mb-2">Pattern</p>
                                            {(consumptionData.spending_breakdown) && Object.entries(consumptionData.spending_breakdown).map(([k, v]) => (
                                                <div key={k} className="flex items-center justify-between text-xs mb-1">
                                                    <span className="text-text-secondary capitalize">{k.replace(/_/g, " ")}</span>
                                                    <span className="text-text-primary font-mono font-semibold">{v.pct}%</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                                            <p className="text-[10px] uppercase tracking-widest text-text-secondary/60 mb-2">Top Signals</p>
                                            <div className="space-y-1.5">
                                                {(consumptionData.signals || []).slice(0, 3).map((s, i) => (
                                                    <div key={i} className="flex items-center justify-between text-xs">
                                                        <span className="text-text-secondary truncate mr-2">{s.name}</span>
                                                        <span className="font-mono font-bold shrink-0" style={{ color: s.color || "#fb923c" }}>+{s.points}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </GlassCard>
                            </motion.div>
                        )}

                        {/* AI Explanation + Persona */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                            {ai && (
                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                                    <GlassCard className="p-6 h-full relative" hover={false} delay={0}>
                                        {regenerating && <RegeneratingVeil provider={activeProvider} />}
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-4">
                                            <Brain size={18} className="text-purple-soft shrink-0" />
                                            <h3 className="font-heading text-lg font-medium text-text-primary">
                                                AI Score Analysis
                                            </h3>
                                            {/* Reads the real provenance instead of a hardcoded vendor name. */}
                                            {ai.generated_by && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-soft/10 text-purple-soft border border-purple-soft/20 font-mono break-all">
                                                    {ai.generated_by}
                                                </span>
                                            )}
                                        </div>
                                        {isDemo && providers.length > 1 && (
                                            <div className="mb-4">
                                                <p className="text-[10px] uppercase tracking-widest text-text-secondary/50 font-semibold mb-1.5">
                                                    Explanation model
                                                </p>
                                                <ModelSwitch
                                                    providers={providers}
                                                    active={activeProvider}
                                                    onSelect={switchProvider}
                                                    busy={regenerating}
                                                />
                                                <p className="text-[10px] text-text-secondary/50 mt-1.5">
                                                    Same score, same signals — only the narration changes.
                                                </p>
                                            </div>
                                        )}
                                        <p className="text-text-secondary text-sm leading-relaxed mb-5">{ai.summary}</p>
                                        {ai.factor_explanations?.length > 0 && (
                                            <div className="space-y-3 mb-5">
                                                {ai.factor_explanations.map((f, i) => (
                                                    <div key={i} className="flex items-start gap-2">
                                                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                                                            f.impact === "positive" ? "bg-green-pioneer" :
                                                            f.impact === "negative" ? "bg-red-starter" : "bg-amber-builder"
                                                        }`} />
                                                        <div>
                                                            <span className="text-text-primary text-xs font-medium">{f.factor}: </span>
                                                            <span className="text-text-secondary text-xs">{f.explanation}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {ai.improvement_suggestions?.length > 0 && !(counterfactual?.levers?.length > 0) && (
                                            <div>
                                                <p className="text-xs uppercase tracking-widest text-text-secondary/60 font-semibold mb-2 flex items-center gap-1.5">
                                                    <Sparkles size={12} className="text-purple-soft" />
                                                    AI Recommendations
                                                </p>
                                                <div className="space-y-1.5">
                                                    {ai.improvement_suggestions.map((s, i) => (
                                                        <div key={i} className="flex items-start gap-2 text-xs">
                                                            <Sparkles size={11} className="text-cyan-signal shrink-0 mt-0.5" />
                                                            <span className="text-text-secondary">{s}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <GenerationMeta
                                            generatedBy={ai.generated_by}
                                            liveGeneration={liveGeneration}
                                            error={regenError}
                                        />
                                        <FaithfulnessPanel faithfulness={faithfulness} />
                                    </GlassCard>
                                </motion.div>
                            )}

                            {persona && (
                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                                    <GlassCard className="p-6 h-full" hover={false} delay={0}>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Users size={18} className="text-cyan-signal" />
                                            <h3 className="font-heading text-lg font-medium text-text-primary">Persona Match</h3>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-text-secondary text-xs mb-1">Matched Cohort</p>
                                                <p className="text-text-primary font-medium text-sm">
                                                    {cohortLabel(persona)}
                                                </p>
                                            </div>
                                            {persona.percentile_range && (
                                                <div>
                                                    <p className="text-text-secondary text-xs mb-1">Your Percentile</p>
                                                    <p className="font-mono text-2xl font-bold text-cyan-signal">
                                                        {persona.percentile_range[0]}-{persona.percentile_range[1]}
                                                        <span className="text-text-secondary text-sm font-normal ml-1">th percentile</span>
                                                    </p>
                                                </div>
                                            )}
                                            <div className="flex gap-4">
                                                <div>
                                                    <p className="text-text-secondary text-xs mb-1">Confidence</p>
                                                    <p className="text-text-primary text-sm font-medium">
                                                        {((persona.confidence || 0) * 100).toFixed(0)}%
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-text-secondary text-xs mb-1">Institution Tier</p>
                                                    <p className="text-text-primary text-sm font-medium">
                                                        {persona.institution_tier?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) || "N/A"}
                                                    </p>
                                                </div>
                                            </div>
                                            {persona.is_inferred && (
                                                <p className="text-purple-soft/60 text-xs flex items-center gap-1">
                                                    <Eye size={11} /> Inferred — connect more signals to improve accuracy
                                                </p>
                                            )}
                                        </div>
                                    </GlassCard>
                                </motion.div>
                            )}
                        </div>

                        {/* What would move this score. Full width: the bars are a
                            ranking, and a ranking squeezed into a half-column column
                            stops being readable at the widths that matter. */}
                        {counterfactual?.levers?.length > 0 && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
                                <GlassCard className="p-6 mb-6" hover={false} delay={0}>
                                    <ScoreLevers counterfactual={counterfactual} />
                                </GlassCard>
                            </motion.div>
                        )}

                        {/* Peer Comparison */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
                            <GlassCard className="p-6 mb-6" hover={false} delay={0}>
                                <div className="flex items-center gap-2 mb-4">
                                    <TrendingUp size={18} className="text-green-pioneer" />
                                    <h3 className="font-heading text-lg font-medium text-text-primary">Peer Comparison</h3>
                                </div>
                                <PeerComparisonBar score={data.score} tier={data.tier} persona={persona} />
                            </GlassCard>
                        </motion.div>

                        {/* Score Trend */}
                        {!isDemo && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="mb-6">
                                <ScoreTrendChart />
                            </motion.div>
                        )}

                        {/* Evidence Breakdown */}
                        {(data.direct_evidence_pct != null || data.inferred_evidence_pct != null) && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
                                <GlassCard className="p-6 mb-6" hover={false} delay={0}>
                                    <div className="flex items-center gap-2 mb-4">
                                        <Layers size={18} className="text-blue-primary" />
                                        <h3 className="font-heading text-lg font-medium text-text-primary">Evidence Quality</h3>
                                    </div>
                                    <div className="flex gap-6 items-center flex-wrap">
                                        <div className="flex-1 min-w-[200px]">
                                            <div className="h-3 bg-white/5 rounded-full overflow-hidden flex">
                                                <div className="h-full bg-green-pioneer rounded-l-full transition-all" style={{ width: `${(data.direct_evidence_pct || 0) * 100}%` }} />
                                                <div className="h-full bg-purple-soft/60 rounded-r-full transition-all" style={{ width: `${(data.inferred_evidence_pct || 0) * 100}%` }} />
                                            </div>
                                        </div>
                                        <div className="flex gap-3 text-xs flex-wrap">
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-pioneer/10 border border-green-pioneer/20">
                                                <div className="w-2.5 h-2.5 rounded-full bg-green-pioneer" />
                                                <span className="text-green-pioneer">Direct: <span className="font-mono text-text-primary font-bold">{((data.direct_evidence_pct || 0) * 100).toFixed(0)}%</span></span>
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-soft/10 border border-purple-soft/20">
                                                <div className="w-2.5 h-2.5 rounded-full bg-purple-soft/70" />
                                                <span className="text-purple-300">Inferred: <span className="font-mono text-text-primary font-bold">{((data.inferred_evidence_pct || 0) * 100).toFixed(0)}%</span></span>
                                            </div>
                                        </div>
                                    </div>
                                </GlassCard>
                            </motion.div>
                        )}

                        {/* Actions Row */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
                            className="flex flex-wrap gap-3" data-testid="actions-row">
                            <Magnet strength={0.15}>
                                <ClickSpark sparkCount={8}>
                                    <button onClick={() => setShowShareCard(true)}
                                        className="bg-gradient-to-r from-indigo-500/20 to-cyan-500/20 text-indigo-300 border border-indigo-500/25 px-5 py-2.5 rounded-xl hover:from-indigo-500/30 hover:to-cyan-500/30 hover:shadow-[0_0_20px_rgba(99,102,241,0.15)] transition-all font-medium text-sm inline-flex items-center gap-2 active:scale-[0.98]"
                                        data-testid="share-score-btn">
                                        <Share2 size={14} /> Share Score Card
                                    </button>
                                </ClickSpark>
                            </Magnet>
                            <Magnet strength={0.15}>
                                <button onClick={handleExportPDF}
                                    className="bg-elevated text-text-primary border border-white/10 px-5 py-2.5 rounded-xl hover:bg-white/5 hover:border-cyan-500/20 hover:shadow-[0_0_15px_rgba(6,182,212,0.1)] transition-all font-medium text-sm inline-flex items-center gap-2 active:scale-[0.98]"
                                    data-testid="export-pdf-btn">
                                    <Download size={14} /> Export PDF
                                </button>
                            </Magnet>
                            <Magnet strength={0.15}>
                                <Link to={`/genz-credit-scoring/lender/${id}`}
                                    className="bg-elevated text-text-primary border border-white/10 px-5 py-2.5 rounded-xl hover:bg-white/5 hover:border-amber-500/20 transition-all font-medium text-sm inline-flex items-center gap-2 active:scale-[0.98]"
                                    data-testid="lender-report-btn">
                                    <CreditCard size={14} /> <ShinyText>View Lender Report</ShinyText>
                                </Link>
                            </Magnet>
                            <Magnet strength={0.2}>
                                <Link to="/genz-credit-scoring/improve"
                                    className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-400 border border-indigo-500/30 px-5 py-2.5 rounded-xl hover:from-indigo-500/30 hover:to-purple-500/30 hover:shadow-[0_0_20px_rgba(99,102,241,0.15)] transition-all font-medium text-sm inline-flex items-center gap-2 active:scale-[0.98]"
                                    data-testid="improve-score-btn">
                                    <ArrowUpRight size={14} /> Improve My Score
                                </Link>
                            </Magnet>
                            <Magnet strength={0.2}>
                                <Link to="/genz-credit-scoring/history"
                                    className="bg-elevated text-text-secondary border border-white/10 px-5 py-2.5 rounded-xl hover:bg-white/5 hover:text-text-primary hover:border-slate-500/30 transition-all font-medium text-sm inline-flex items-center gap-2 active:scale-[0.98]"
                                    data-testid="score-history-btn">
                                    <Clock3 size={14} /> View Score History
                                </Link>
                            </Magnet>
                        </motion.div>
                    </div>
                </div>
            )}

            {/* Shareable Score Card Modal */}
            <AnimatePresence>
                {showShareCard && data && (
                    <ShareableScoreCard
                        score={data.score}
                        tier={data.tier}
                        confidence={data.confidence}
                        name={data.name}
                        signals={data.signal_scores}
                        onClose={() => setShowShareCard(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
